/**
 * @genesis_domain Feedback & Error States
 * @genesis_version V9.1
 * @genesis_intent React Router errorElement boundary with Dual-Glass Protocol styling and useRouteError() integration.
 * @genesis_boundaries This component is FORBIDDEN from containing business logic, API calls, or non-error UI.
 * @genesis_ai_trail V77.0: Created as part of GENESIS V77 Safety Net Audit. Implements V65 Dual-Glass Protocol with React Router 6.4+ error handling.
 */

import React from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';

export const RouteErrorBoundary: React.FC = () => {
    const error = useRouteError();
    const navigate = useNavigate();

    console.error('RouteErrorBoundary caught an error:', error);

    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
    const statusCode = (error as Record<string, unknown>)?.status as number || (error as Record<string, unknown>)?.statusCode as number || 500;

    const handleGoHome = () => {
        navigate('/', { replace: true });
    };

    const handleReload = () => {
        window.location.reload();
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[var(--color-primary-base)]/5 to-[var(--color-bg-base)]">
            {/* Surface Anchor Layer - Prevents "washed out" look */}
            <div className="absolute inset-0 z-[-1] bg-gradient-to-b from-transparent to-[var(--color-primary-base)]/10 pointer-events-none" />

            <div className="relative w-full max-w-md">
                {/* Mobile: Studio Equilibrium (Dark glass, inverted text) */}
                <div className="md:hidden rounded-2xl theme-card backdrop-blur-3xl border border-[var(--color-glass-border)] shadow-[0_0_25px_var(--color-primary-base)/40%,inset_0_1px_0_rgba(255,255,255,0.6)] p-8">
                    <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 rounded-full bg-[var(--color-danger-subtle)] flex items-center justify-center mb-6">
                            <svg
                                className="w-8 h-8 text-[var(--color-danger-base)]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                />
                            </svg>
                        </div>

                        <h1 className="text-2xl font-bold text-[var(--color-card-text)] mb-2 font-sans tracking-tight">
                            {statusCode === 404 ? 'Page Not Found' : 'Something Went Wrong'}
                        </h1>

                        <p className="text-[var(--color-content-secondary)] mb-6 font-sans">
                            {statusCode === 404
                                ? "The page you're looking for doesn't exist or has been moved."
                                : 'We encountered an unexpected error while loading this page.'}
                        </p>

                        {statusCode !== 404 && (
                            <div className="w-full mb-6 p-4 bg-[var(--color-panel-overlay)] rounded-lg">
                                <p className="text-sm font-medium text-[var(--color-card-text)] mb-1 font-sans">Error details:</p>
                                <code className="text-xs text-[var(--color-content-secondary)] font-mono break-words">
                                    {errorMessage}
                                </code>
                            </div>
                        )}

                        <div className="flex flex-col w-full gap-3">
                            <button
                                onClick={handleGoHome}
                                className="w-full px-6 py-3 bg-[var(--color-surface-elevated)] text-[var(--color-card-text)] font-medium rounded-lg hover:bg-[var(--color-surface-hover)] transition-all duration-200 active:scale-95 font-sans h-14 min-h-[56px] flex items-center justify-center"
                            >
                                Go to Home
                            </button>

                            <button
                                onClick={handleReload}
                                className="w-full px-6 py-3 bg-[var(--color-primary-base)]/40 text-[var(--color-content-inverse)] font-medium rounded-lg hover:bg-[var(--color-primary-base)]/50 transition-all duration-200 active:scale-95 font-sans h-14 min-h-[56px] flex items-center justify-center"
                            >
                                Reload Page
                            </button>
                        </div>
                    </div>
                </div>

                {/* Desktop: Luminous Frost (Light glass, chromatic inversion) */}
                <div className="hidden md:block rounded-2xl theme-card backdrop-blur-3xl border border-[var(--color-glass-border)] shadow-[0_20px_50px_rgba(79,70,229,0.15)] p-8">
                    <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 rounded-full bg-[var(--color-danger-subtle)] flex items-center justify-center mb-6">
                            <svg
                                className="w-8 h-8 text-[var(--color-danger-base)]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                />
                            </svg>
                        </div>

                        <h1 className="text-2xl font-bold text-[var(--color-card-text)] mb-2 font-sans tracking-tight">
                            {statusCode === 404 ? 'Page Not Found' : 'Something Went Wrong'}
                        </h1>

                        <p className="text-[var(--color-content-secondary)] mb-6 font-sans">
                            {statusCode === 404
                                ? "The page you're looking for doesn't exist or has been moved."
                                : 'We encountered an unexpected error while loading this page.'}
                        </p>

                        {statusCode !== 404 && (
                            <div className="w-full mb-6 p-4 bg-[var(--color-panel-overlay)] rounded-lg">
                                <p className="text-sm font-medium text-[var(--color-card-text)] mb-1 font-sans">Error details:</p>
                                <code className="text-xs text-[var(--color-content-tertiary)] font-mono break-words">
                                    {errorMessage}
                                </code>
                            </div>
                        )}

                        <div className="flex gap-3 w-full">
                            <button
                                onClick={handleGoHome}
                                className="flex-1 px-6 py-3 bg-[var(--color-surface-elevated)] text-[var(--color-content-secondary)] font-medium rounded-lg hover:bg-[var(--color-surface-hover)] transition-all duration-200 hover:shadow-md font-sans h-14 min-h-[56px] flex items-center justify-center"
                            >
                                Go to Home
                            </button>

                            <button
                                onClick={handleReload}
                                className="flex-1 px-6 py-3 bg-[var(--color-primary-base)] text-[var(--color-content-inverse)] font-medium rounded-lg hover:bg-[var(--color-primary-hover)] transition-all duration-200 hover:shadow-md hover:ring-[var(--color-primary-base)]/20 font-sans h-14 min-h-[56px] flex items-center justify-center"
                            >
                                Reload Page
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};